// src/components/charts/StatisticsChart.jsx
import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

const StatisticsChart = ({ 
  data = [], 
  metric, 
  units = {},
  color = '#8884d8' 
}) => {
  // Calculate basic statistics
  const calculateStats = () => {
    if (!data || data.length === 0) {
      return {
        min: 0,
        max: 0,
        mean: 0,
        median: 0
      };
    }

    const values = data.map(d => d[metric] || 0).filter(v => !isNaN(v));
    const sortedValues = [...values].sort((a, b) => a - b);
    
    return {
      min: Math.min(...values),
      max: Math.max(...values),
      mean: values.reduce((acc, val) => acc + val, 0) / values.length,
      median: sortedValues[Math.floor(sortedValues.length / 2)]
    };
  };

  // Calculate histogram data
  const calculateHistogram = (inputData, binCount = 10) => {
    if (!inputData || inputData.length === 0) return [];

    const values = inputData.map(d => d[metric]).filter(value => value != null);
    if (values.length === 0) return [];

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    const binWidth = range / binCount;

    // Initialize bins
    const histogram = Array(binCount).fill(0).map((_, i) => ({
      binStart: min + (i * binWidth),
      binEnd: min + ((i + 1) * binWidth),
      count: 0,
      name: `${(min + (i * binWidth)).toFixed(1)}-${(min + ((i + 1) * binWidth)).toFixed(1)}`
    }));

    // Fill bins
    values.forEach(value => {
      const binIndex = Math.min(Math.floor((value - min) / binWidth), binCount - 1);
      if (binIndex >= 0 && binIndex < binCount) {
        histogram[binIndex].count++;
      }
    });

    return histogram;
  };

  // Get unit symbol helper function
  const getUnitSymbol = (metricName) => {
    const unitMap = {
      temperature: units.temperature === 'C' ? '°C' : '°F',
      humidity: '%',
      pressure: units.pressure === 'hPa' ? 'hPa' : 'inHg',
      dewPoint: units.temperature === 'C' ? '°C' : '°F',
      heatIndex: units.temperature === 'C' ? '°C' : '°F',
      wetBulb: units.temperature === 'C' ? '°C' : '°F',
      absoluteHumidity: 'g/m³',
      airDensity: 'kg/m³'
    };
    return unitMap[metricName] || '';
  };

  const stats = calculateStats();
  const histogramData = calculateHistogram(data);

  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(stats).map(([key, value]) => (
          <div key={key} className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500 capitalize">{key}</div>
            <div className="text-lg font-semibold">
              {value.toFixed(2)} {getUnitSymbol(metric)}
            </div>
          </div>
        ))}
      </div>

      {histogramData.length > 0 ? (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart 
            data={histogramData}
            margin={{ top: 20, right: 30, left: 60, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
              tick={{ fontSize: 12 }}
              label={{ 
                value: `${metric} (${getUnitSymbol(metric)})`,
                position: 'insideBottom',
                offset: -40
              }}
            />
            <YAxis
              label={{ 
                value: 'Frequency',
                angle: -90,
                position: 'insideLeft',
                offset: -40
              }}
              tick={{ fontSize: 12 }}
            />
            <Tooltip 
              formatter={(value, name) => [
                value, 
                name === 'count' ? 'Frequency' : name
              ]}
              contentStyle={{ fontSize: '12px' }}
            />
            <Legend 
              verticalAlign="top" 
              height={36}
              wrapperStyle={{ fontSize: '12px' }}
            />
            <ReferenceLine 
              x={stats.mean.toFixed(1)} 
              stroke="red" 
              label={{ 
                value: "Mean",
                position: 'top',
                fontSize: 12
              }} 
            />
            <ReferenceLine 
              x={stats.median.toFixed(1)} 
              stroke="blue" 
              label={{ 
                value: "Median",
                position: 'bottom',
                fontSize: 12
              }} 
            />
            <Bar 
              dataKey="count" 
              fill={color} 
              name={`${metric} Distribution`}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-64 flex items-center justify-center">
          <p className="text-gray-500">Insufficient data for histogram</p>
        </div>
      )}
    </div>
  );
};

export default StatisticsChart;